import { FrameworkVersion } from '../framework/frameworkClasses';

export interface ProjectVersion {
  major: number;
  minor: number;
  patch: number;
}

export interface ProjectVersionFields {
  version?: ProjectVersion;
  frameworkVersion?: ProjectVersion;
}

type LegacyProjectVersionFields = ProjectVersionFields & {
  framework_version?: unknown;
};

const DEFAULT_FRAMEWORK_VERSION: ProjectVersion = parsePartialVersionString(FrameworkVersion) ?? {
  major: 0,
  minor: 0,
  patch: 0,
};

export const CURRENT_FRAMEWORK_VERSION: ProjectVersion = Object.freeze(DEFAULT_FRAMEWORK_VERSION);

export const CURRENT_PROJECT_VERSION: ProjectVersion = Object.freeze({
  major: 1,
  minor: 0,
  patch: 0,
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

export function cloneProjectVersion(version: ProjectVersion): ProjectVersion {
  return {
    major: version.major,
    minor: version.minor,
    patch: version.patch,
  };
}

export function isProjectVersion(value: unknown): value is ProjectVersion {
  if (!isRecord(value)) return false;

  const candidate = value as Partial<ProjectVersion>;
  return [candidate.major, candidate.minor, candidate.patch].every((part) => typeof part === 'number' && Number.isInteger(part) && part >= 0);
}

function normalizeProjectVersion(value: unknown): ProjectVersion | undefined {
  if (isProjectVersion(value)) return value;
  const parsed = parseProjectVersionString(value) ?? parsePartialVersionString(value);
  return parsed ?? undefined;
}

export function pickProjectVersionFields(value: unknown): ProjectVersionFields {
  if (!isRecord(value)) return {};

  const candidate = value as LegacyProjectVersionFields;
  const version = normalizeProjectVersion(candidate.version);
  const frameworkVersion = normalizeProjectVersion(candidate.frameworkVersion) ?? normalizeProjectVersion(candidate.framework_version);

  return {
    ...(version && { version }),
    ...(frameworkVersion && { frameworkVersion }),
  };
}

export function applyProjectVersionDefaults<T extends object>(value: T): Omit<T, 'framework_version'> & Required<ProjectVersionFields> {
  const rest = { ...(value as Record<string, unknown>) };
  delete rest.framework_version;
  const versions = pickProjectVersionFields(value);

  return {
    ...(rest as Omit<T, 'framework_version'>),
    version: versions.version ? cloneProjectVersion(versions.version) : cloneProjectVersion(CURRENT_PROJECT_VERSION),
    frameworkVersion: resolveFrameworkVersion(versions.frameworkVersion),
  };
}

export function projectVersionToString(version: ProjectVersion): string {
  return `${version.major}.${version.minor}.${version.patch}`;
}

export function compareProjectVersions(left: ProjectVersion, right: ProjectVersion): number {
  if (left.major !== right.major) return left.major - right.major;
  if (left.minor !== right.minor) return left.minor - right.minor;
  return left.patch - right.patch;
}

function minProjectVersion(left: ProjectVersion, right: ProjectVersion): ProjectVersion {
  return compareProjectVersions(left, right) <= 0 ? cloneProjectVersion(left) : cloneProjectVersion(right);
}

/** Defaults missing values to the current framework and caps values above {@link FrameworkVersion}. */
export function resolveFrameworkVersion(value?: unknown): ProjectVersion {
  const parsed = normalizeProjectVersion(value) ?? CURRENT_FRAMEWORK_VERSION;
  return minProjectVersion(parsed, CURRENT_FRAMEWORK_VERSION);
}

export function resolveDeployFrameworkVersionString(value?: unknown): string {
  return projectVersionToString(resolveFrameworkVersion(value));
}

export function parseProjectVersionString(value: unknown): ProjectVersion | null {
  if (typeof value !== 'string') return null;

  const match = /^v?(\d+)\.(\d+)\.(\d+)$/.exec(value.trim());
  if (!match) return null;

  return {
    major: Number.parseInt(match[1], 10),
    minor: Number.parseInt(match[2], 10),
    patch: Number.parseInt(match[3], 10),
  };
}

/** Parse a version string that may omit minor/patch; missing parts default to 0. */
export function parsePartialVersionString(value: unknown): ProjectVersion | null {
  if (typeof value !== 'string') return null;

  const parts = value.trim().replace(/^v/i, '').split('.');
  if (parts.length === 0 || parts.length > 3 || parts.some((part) => !/^\d+$/.test(part))) return null;

  const [major = '0', minor = '0', patch = '0'] = parts;
  return {
    major: Number.parseInt(major, 10),
    minor: Number.parseInt(minor, 10),
    patch: Number.parseInt(patch, 10),
  };
}

export function migratePersistedProjectVersionState<T>(state: T): T {
  if (!isRecord(state)) return state;

  const candidate = state as Record<string, unknown>;
  return {
    ...candidate,
    ...(Array.isArray(candidate.projects) && {
      projects: candidate.projects.map((project) => (isRecord(project) ? applyProjectVersionDefaults(project) : project)),
    }),
    ...(isRecord(candidate.loadedProject) && {
      loadedProject: applyProjectVersionDefaults(candidate.loadedProject),
    }),
  } as T;
}
